import React, { useCallback, useState } from 'react'
import { isEmpty } from 'lodash'
import { Grid, Box, Typography, Button } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { PlusCircle } from 'react-feather'

import { AlertDialog, ContentHeader, Dai } from 'components'
import { PlanActionForm, CardType } from '../'

import useSnackbar from 'hooks/useSnackbar'

import useStyles from './styles'
import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

const PlanAction = ({
  planActions = [],
  questionControl,
  refresh,
  isEdit,
  setLoading,
}) => {
  const [planActionId, setPlanActionId] = useState(null)
  const [loadingDai, setLoadingDai] = useState(false)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const { TAGS } = constants.dai.solutions

  const handleCloseDialog = () => setPlanActionId(null)

  const handleCreatePlan = async () => {
    try {
      setLoading(true)

      await service.dponet.questionControls.createPlan({
        questionControlId: questionControl?.id,
        kind: 'plan_action',
      })

      refresh()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error.response.data.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeletePlanAction = async () => {
    try {
      setLoading(true)

      await service.dponet.questionControls.deletePlanAction({
        questionPlanActionEvidencyId: planActionId,
      })

      snackbar.open({
        message: 'Plano de ação excluído com sucesso!',
        variant: 'success',
      })

      refresh()
      handleCloseDialog()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu um erro ao excluir o plano de ação',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const generatePlanActions = async () => {
    try {
      await service.dponet.questionControls.generateQuestionPlanActionEvicencies(
        questionControl?.id,
      )

      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error.response.data.error) ||
          'Ocorreu um erro ao gerar o plano de ação, por favor, tente novamente.',
        variant: 'error',
      })
    } finally {
      setLoadingDai(false)
    }
  }

  const handleClickDai = () => {
    if (loadingDai) return

    setLoadingDai(true)

    generatePlanActions()
  }

  const hasPlanActionGeneratedByAi = useCallback(() => {
    return planActions.some((action) => action.generatedByAi)
  }, [planActions])

  return (
    <Box mt={2}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ContentHeader
            titleComponent={
              <Typography variant="h4" className={classes.planActionTitle}>
                Plano de ação
              </Typography>
            }
          >
            <Dai.Permitted tag={TAGS.GENERATE_PLAN_ACTION}>
              <Dai.StarButton
                size="small"
                onClick={handleClickDai}
                disabled={loadingDai || hasPlanActionGeneratedByAi() || !isEdit}
                disabledTooltipTitle={
                  loadingDai
                    ? 'Gerando planos de ação'
                    : 'Para gerar novos planos de ação com a DAI, é preciso remover os que já existem'
                }
                automaticWidth
              >
                ADICIONAR COM A DAI
              </Dai.StarButton>
            </Dai.Permitted>

            <Button
              startIcon={<PlusCircle size={20} />}
              variant="contained"
              type="button"
              color="primary"
              size="small"
              disabled={!isEdit || !questionControl?.openWrite}
              onClick={handleCreatePlan}
            >
              ADICIONAR MANUALMENTE
            </Button>
          </ContentHeader>
        </Grid>
        {loadingDai && (
          <Grid item xs={12}>
            <Dai.Loading
              texts={constants.dai.questionControls.LOADING_TEXTS}
              loading
            />
          </Grid>
        )}
        {isEmpty(planActions) && !loadingDai && (
          <Grid item xs={12}>
            <Alert severity="info" variant="outlined">
              <Typography>
                Nenhum plano de ação foi detectado. Você pode adicionar um plano
                de ação automaticamente, utilizando nossa IA, clicando em
                <strong> Adicionar com a DAI</strong>.
              </Typography>
            </Alert>
          </Grid>
        )}
        {!questionControl?.openWrite && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <Typography>
                Para editar este plano de ação, é necessário reabri-lo.
              </Typography>
            </Alert>
          </Grid>
        )}

        {planActions?.map((planAction) => (
          <Grid item xs={12} key={planAction?.id}>
            <CardType
              planAction={planAction}
              setPlanActionId={setPlanActionId}
              isEdit={isEdit}
            >
              <PlanActionForm
                planAction={planAction}
                isEdit={isEdit}
                refresh={refresh}
                questionControl={questionControl}
              />
            </CardType>
          </Grid>
        ))}
      </Grid>
      <AlertDialog
        open={!!planActionId}
        onClose={handleCloseDialog}
        variant="standard"
        severity="warning"
        content="Tem certeza de que deseja excluir este plano de ação? Essa ação não poderá ser desfeita!"
        dialogActions={
          <>
            <Button
              variant="outlined"
              color="primary"
              className={classes.outlinedWarningButton}
              onClick={handleCloseDialog}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.containerWarningButton}
              onClick={handleDeletePlanAction}
            >
              Excluir
            </Button>
          </>
        }
      />
    </Box>
  )
}

export default PlanAction
